package io.jsonize.web.screens.editor

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import io.jsonize.web.theme.JsonTheme

/**
 * Updated Error Banner.
 *
 * Supports:
 * - JsonError (line/column)
 * - Generic text error (string)
 * - Click-to-jump-to-offset
 */

data class JsonError(
    val message: String,
    val line: Int? = null,
    val column: Int? = null,
    val offset: Int? = null
)

@Composable
fun EditorErrorBanner(
    error: JsonError? = null,
    customMessage: String? = null,
    text: String,
    onJumpTo: (offset: Int) -> Unit
) {
    val colors = JsonTheme.colors

    if (error == null && customMessage == null) return

    val isJsonError = error != null
    val bgColor = Color(0xFF3B1B1B)
    val textColor = Color(0xFFFFCDD2)

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 4.dp)
            .clickable(
                enabled = isJsonError && error?.line != null && error.column != null
            ) {
                // If JSON error, compute the offset and jump
                if (error != null && error.line != null && error.column != null) {
                    val offset = computeOffsetFromLineColumn(text, error.line, error.column)
                    onJumpTo(offset)
                }
            },
        tonalElevation = 1.dp,
        color = bgColor,
        contentColor = textColor
    ) {
        Column(modifier = Modifier.padding(10.dp)) {

            Text(
                text = if (isJsonError) "Invalid JSON" else "Error",
                style = MaterialTheme.typography.labelLarge
            )

            Spacer(Modifier.height(2.dp))

            // Show error message
            Text(
                text = when {
                    isJsonError -> error!!.message
                    else -> customMessage!!
                },
                style = MaterialTheme.typography.bodySmall
            )

            // Show jump target if available
            if (isJsonError && error!!.line != null && error.column != null) {
                Spacer(Modifier.height(4.dp))
                Text(
                    text = "Click to jump → Line ${error.line}, Column ${error.column}",
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }
    }
}

/**
 * Convert (line, col) → offset index in string.
 */
private fun computeOffsetFromLineColumn(text: String, line: Int, col: Int): Int {
    if (line <= 1 && col <= 1) return 0
    var currentLine = 1
    var index = 0

    // Move through text until the requested line
    while (index < text.length && currentLine < line) {
        if (text[index] == '\n') currentLine++
        index++
    }

    // Move to the requested column
    var colCounter = 1
    while (index < text.length && colCounter < col && text[index] != '\n') {
        index++
        colCounter++
    }

    return index.coerceIn(0, text.length)
}
